import './toolbar.css';
import {
  commandState,
  isCommandAllowed,
  isCommandServed,
  onCommandStateChange,
  presetMenuEntries,
  runCommand,
  supportsMpr,
  toolCommand,
  viewerFlags,
  type CommandId,
  type CommandState,
  type ViewerFlags,
} from '../commands';
import { lockToolsLabel } from '../display';
import type { ViewerShell } from '../shell';
import { currentLeftTool, onLeftToolChange, type ToolId } from '../tools';
import { mountCineBar } from './cine';
import { isCompactChrome, stripShowsPanZoom } from './compact';
import { closeMenus, showMenu, type MenuItem } from './contextmenu';

/**
 * Main toolbar (spec section 2, rows 1–26, left to right) plus the "Estudios" drawer toggle at the
 * left of the top bar. Every button runs a command from the shared registry.
 */

/** Bootstrap `lg` breakpoint: the 3x3 layout is offered only at or above it (`visible-lg`). */
const LARGE_SCREEN_PX = 1200;
const DROPDOWN_GAP_PX = 2;

type ToggleKey = keyof Pick<CommandState, 'linked' | 'manualLinked' | 'referenceLines'>;

interface ToolDef {
  tool: ToolId;
  label: string;
  title: string;
  id: string;
}

const LAYOUTS: Array<{ rows: number; columns: number; largeOnly?: boolean }> = [
  { rows: 1, columns: 1 },
  { rows: 1, columns: 2 },
  { rows: 2, columns: 1 },
  { rows: 2, columns: 2 },
  { rows: 3, columns: 3, largeOnly: true },
];

const ALIGN_ITEMS: Array<[label: string, command: CommandId]> = [
  ['Izquierda', 'align:Left'],
  ['Derecha', 'align:Right'],
  ['Arriba', 'align:Top'],
  ['Abajo', 'align:Bottom'],
  ['Arriba-Izq', 'align:TopLeft'],
  ['Abajo-Izq', 'align:BottomLeft'],
  ['Arriba-Der', 'align:TopRight'],
  ['Abajo-Der', 'align:BottomRight'],
];

const ROI_TOOLS: ToolDef[] = [
  { tool: 'ellipticalRoi', label: 'ROI Elíptico', title: '(b) ROI Elíptica', id: 'roi-elliptical' },
  { tool: 'rectangleRoi', label: 'ROI Rectangular', title: 'ROI Rectangular', id: 'roi-rectangle' },
  { tool: 'freehandMouseTool', label: 'ROI Poligonal', title: 'ROI Poligonal', id: 'roi-polygon' },
];

const MEASURE_TOOLS: ToolDef[] = [
  { tool: 'Bidirectional', label: 'Bidireccional', title: 'Medida Bidireccional', id: 'bidirectional' },
  { tool: 'cardiothoracicIndex', label: 'Índice Cardiotorácico', title: 'Índice Cardiotorácico', id: 'cardiothoracic' },
  { tool: 'parallelDistance', label: 'Distancia entre líneas Paralelas', title: 'Distancia entre líneas Paralelas', id: 'parallel' },
  { tool: 'cobbs', label: 'Ángulos de Cobb', title: '(c) Medir ángulos de Cobb', id: 'cobbs' },
  { tool: 'salvati', label: 'Insall Salvati', title: 'Proporción entre dos líneas', id: 'salvati' },
  { tool: 'coxometry', label: 'Coxometría', title: 'Coxometría', id: 'coxometry' },
  { tool: 'cobbAngleMV', label: 'Anteversión Femoral', title: '(c) Medida Femoral', id: 'femoral' },
  { tool: 'arrowAnnotate', label: 'Flecha', title: '(c) Medida Indicador', id: 'arrow' },
  { tool: 'textMarker', label: 'Marcas Columna', title: '(c) Marcación de Columna', id: 'spine' },
  { tool: 'text', label: 'Texto', title: '(c) Texto', id: 'text' },
];

const LINE_TOOL: ToolDef = { tool: 'lines', label: 'Línea', title: '(l) Medir distancia con una línea', id: 'lines' };
const ANGLE_TOOL: ToolDef = { tool: 'angles', label: 'Ángulo', title: '(a) Medir ángulos', id: 'angles' };
const PAN_TOOL: ToolDef = { tool: 'pan', label: 'Mover', title: '(p) Mover la imagen', id: 'pan' };
const ZOOM_TOOL: ToolDef = { tool: 'zoom', label: 'Magnificar', title: '(z) Magnificar o alejar', id: 'zoom' };
const MAGNIFY_TOOL: ToolDef = { tool: 'magnify', label: 'Lupa', title: '(m) Magnifica zona de interés', id: 'magnify' };
const FLIP_H = { label: 'Espejo H.', title: 'Invertir izquierda y derecha', command: 'flip-h' as CommandId, id: 'hflip' };
const FLIP_V = { label: 'Espejo V.', title: 'Invertir arriba y abajo', command: 'flip-v' as CommandId, id: 'vflip' };
const MAXIMIZE = { label: 'Maximizar', title: '(doble click) vista en pantalla completa', command: 'full-screen' as CommandId, id: 'full-screen' };

function button(parent: HTMLElement, id: string, label: string, title: string, onClick: (el: HTMLButtonElement) => void): HTMLButtonElement {
  const el = document.createElement('button');
  el.type = 'button';
  el.id = `tb-${id}`;
  el.className = 'toolbar-button';
  el.textContent = label;
  if (title) el.title = title;
  el.addEventListener('click', () => onClick(el));
  parent.appendChild(el);
  return el;
}

function commandButton(parent: HTMLElement, id: string, label: string, title: string, command: CommandId): HTMLButtonElement {
  const el = button(parent, id, label, title, () => runCommand(command));
  el.dataset.command = command;
  return el;
}

function toolButton(parent: HTMLElement, def: ToolDef): HTMLButtonElement {
  const el = commandButton(parent, def.id, def.label, def.title, toolCommand(def.tool));
  el.dataset.tool = def.tool;
  return el;
}

function toggleButton(parent: HTMLElement, id: string, label: string, title: string, command: CommandId, key: ToggleKey): HTMLButtonElement {
  const el = commandButton(parent, id, label, title, command);
  el.classList.add('toggle');
  el.dataset.toggle = key;
  el.setAttribute('aria-pressed', 'false');
  return el;
}

/** A button that opens a popup menu under itself; `items` is re-evaluated on every open. */
function dropdown(host: HTMLElement, parent: HTMLElement, id: string, label: string, title: string, items: () => MenuItem[], tools: ToolId[] = []): HTMLButtonElement {
  const el = button(parent, id, label, title, (self) => {
    if (self.getAttribute('aria-expanded') === 'true') {
      closeMenus();
      return;
    }
    const rect = self.getBoundingClientRect();
    showMenu(host, items(), rect.left, rect.bottom + DROPDOWN_GAP_PX, { className: `tb-menu tb-menu-${id}`, idPrefix: 'tb-', owner: self });
  });
  el.classList.add('dropdown');
  el.setAttribute('aria-haspopup', 'menu');
  el.setAttribute('aria-expanded', 'false');
  if (tools.length > 0) el.dataset.tools = tools.join(' ');
  return el;
}

function toolItem(def: ToolDef): MenuItem {
  return { id: `${def.id}-link`, label: def.label, title: def.title, command: toolCommand(def.tool), active: currentLeftTool() === def.tool };
}

function layoutItems(): MenuItem[] {
  return LAYOUTS
    .filter((layout) => !layout.largeOnly || window.innerWidth >= LARGE_SCREEN_PX)
    .map(({ rows, columns }) => ({ id: `layout-${rows}x${columns}`, label: `${rows}x${columns}`, title: `${rows}x${columns}`, command: `layout:${rows}x${columns}` as CommandId }));
}

function windowItems(shell: ViewerShell): MenuItem[] {
  return [
    { id: 'window-interactive', label: 'Interactivo', command: toolCommand('window-level'), active: currentLeftTool() === 'window-level' },
    { divider: true, label: '' },
    ...presetMenuEntries(shell),
  ];
}

function rotateItems(): MenuItem[] {
  return [
    commandItem('rotate-90', 'Rotar 90°', 'rotar la imagen contra las manecillas del reloj', 'rotate'),
    { id: 'rotate-free-link', label: 'Rotación libre', title: 'arrastre para girar la imagen libremente', command: toolCommand('rotate-free'), active: currentLeftTool() === 'rotate-free' },
  ];
}

function measureItems(flags: ViewerFlags): MenuItem[] {
  const items = MEASURE_TOOLS.map(toolItem);
  if (flags.flipToolsInTopMenu) items.push(toolItem(LINE_TOOL), toolItem({ ...ANGLE_TOOL, label: 'Ángulos' }));
  return items;
}

function commandItem(id: string, label: string, title: string, command: CommandId): MenuItem {
  return { id: `${id}-link`, label, title, command };
}

/** "Más" (⋮) entries (spec section 2, More table), with the config gates IMEXHS applies. */
/** The strip buttons the compact chrome hides (`tb-compact-hide`), offered here instead. */
function compactItems(state: CommandState): MenuItem[] {
  return [
    { id: 'link-link', label: 'Sincronizar', title: 'Sincronizar series', command: 'toggle-link', active: state.linked },
    { id: 'manual-link-link', label: 'Manual', title: 'Sincronizar series de diferentes estudios a partir de posición inicial', command: 'toggle-manual-link', active: state.manualLinked },
    { id: 'align-link', label: 'Alinear', title: 'Alinear la imagen', submenu: () => ALIGN_ITEMS.map(([label, command]) => ({ id: `${command.replace(':', '-').toLowerCase()}-link`, label, command })) },
    { id: 'triangulation-link', label: 'Triangulación', title: '(t) Triangulación (Herramienta 3D)', command: toolCommand('triangulation'), active: currentLeftTool() === 'triangulation' },
    { divider: true, label: '' },
  ];
}

function moreItems(shell: ViewerShell, flags: ViewerFlags): MenuItem[] {
  const state = commandState();
  const narrow = window.innerWidth < LARGE_SCREEN_PX;
  const compact = isCompactChrome();
  const items: MenuItem[] = compact ? compactItems(state) : [];
  if (supportsMpr(shell)) {
    items.push(
      commandItem('mpr', 'MPR (legacy)', 'Reconstrucción Multiplanar', 'mpr'),
      commandItem('mip', 'MIP', 'MIP', 'mip'),
      commandItem('volume-3d', '3D', 'Reconstrucción de volumen 3D', 'volume-3d'),
    );
  }
  items.push(
    { id: 'help-link', label: 'Ayuda', title: 'Guía rápida', command: 'help-dialog' },
    { id: 'download-link', label: 'Descargar', title: 'Descargar imagen con mediciones', command: 'download-dialog' },
    { id: 'reset-link', label: 'Reiniciar Vista', title: '(r) Reiniciar ventana y posición', command: 'reset' },
    { id: 'hide-annotations-link', label: 'Ocultar datos Dicom', title: '(h) Ocultar información DICOM', command: 'toggle-overlay', active: !state.overlayVisible },
  );
  if (!flags.flipToolsInTopMenu) {
    items.push(
      { id: `${FLIP_H.id}-link`, label: FLIP_H.label, title: FLIP_H.title, command: FLIP_H.command },
      { id: `${FLIP_V.id}-link`, label: FLIP_V.label, title: FLIP_V.title, command: FLIP_V.command },
    );
  }
  if (narrow) {
    // Below 1200 px the wide-only buttons live here; a touch strip shows Mover and Magnificar itself.
    items.push({ id: `${MAXIMIZE.id}-link`, label: MAXIMIZE.label, title: MAXIMIZE.title, command: MAXIMIZE.command, active: state.fullScreen });
    if (!stripShowsPanZoom()) items.push(toolItem(PAN_TOOL), toolItem(ZOOM_TOOL));
    items.push(toolItem(MAGNIFY_TOOL));
  }
  items.push({ id: 'reload-link', label: 'Recargar', title: 'Recargar todos los datos de la imagen desde el servidor', command: 'reload' });
  if (flags.stowUrl && flags.uploadEnabled) items.push(commandItem('capture', 'Captura Dicom', 'Almacenar en el servidor como captura secundaria', 'secondary-capture'));
  items.push(
    { id: 'cine-link', label: 'Cine', title: 'Mostrar controles de cine', command: 'cine-bar-toggle', active: state.cineBarVisible },
    { id: 'clear-link', label: 'Limpiar', title: 'Borrar todas las mediciones', command: 'clear-all' },
  );
  if (flags.downloadDicomEnabled) items.push(commandItem('download-dicom', 'Descargar Dicom', 'Descargar Archivo DICOM', 'download-dicom'));
  items.push(commandItem('attach-pdf', 'Adjuntar PDF', 'Adjuntar Archivo PDF', 'attach-pdf'));
  // Hidden unless the session may share and the exam was opened from the hub (src/ris/share.ts).
  if (isCommandServed('share-study')) items.push(commandItem('share', 'Compartir', 'Crear un enlace temporal a estas imágenes', 'share-study'));
  if (flags.multiScreenOption) items.push(commandItem('multi-screen', 'Múltiples pantallas', 'Opción de visualización de múltiples pantallas', 'multi-screen'));
  if (flags.settingsEnabled) items.push(commandItem('settings', 'Configuración', 'Configuración', 'settings'));
  const lock = lockToolsLabel();
  items.push(
    { ...commandItem('lock-tools', lock.label, lock.title, 'lock-tools'), active: state.toolsLocked },
    commandItem('reset-config', 'Reiniciar Config', 'Restablecer la configuración original del visor', 'reset-config'),
  );
  if (flags.hangingEnabled) items.push(commandItem('hanging', 'Ajustes H. Protocol', 'Ajustes H. Protocol', 'hanging-protocol'));
  // Entries the session has no permission for are hidden (ris viewer ticket perms).
  return items.filter((item) => !item.command || isCommandAllowed(item.command));
}

/** Highlights the active tool, dropdowns holding it, and toggles that are on. */
function refresh(host: HTMLElement): void {
  const tool = currentLeftTool();
  const state = commandState();
  host.querySelectorAll<HTMLElement>('[data-tool]').forEach((el) => el.classList.toggle('active', el.dataset.tool === tool));
  host.querySelectorAll<HTMLElement>('[data-tools]').forEach((el) => {
    el.classList.toggle('active', (el.dataset.tools ?? '').split(' ').includes(tool));
  });
  host.querySelectorAll<HTMLElement>('[data-toggle]').forEach((el) => {
    const on = state[el.dataset.toggle as ToggleKey];
    el.classList.toggle('active', on);
    el.setAttribute('aria-pressed', String(on));
  });
  host.querySelector('#tb-full-screen')?.classList.toggle('active', state.fullScreen);
}

function installStudiesToggle(shell: ViewerShell): void {
  const { topNav, toolbar } = shell.regions;
  topNav.querySelector('#btn-sidebar')?.remove();
  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.id = 'btn-sidebar';
  toggle.className = 'toolbar-button studies-toggle';
  toggle.textContent = 'Estudios';
  const sync = () => {
    const open = shell.isLeftDrawerOpen();
    toggle.title = open ? 'Ocultar estudios' : 'Mostrar estudios';
    toggle.setAttribute('aria-pressed', String(open));
    toggle.classList.toggle('active', open);
  };
  toggle.addEventListener('click', () => {
    shell.setLeftDrawer(!shell.isLeftDrawerOpen());
    sync();
  });
  toggle.setAttribute('aria-label', 'Estudios');
  topNav.insertBefore(toggle, toolbar);
  // The compact chrome closes the drawer on its own (a series pick, the scrim): follow the shell.
  shell.on('drawer-changed', sync);
  sync();
}

/** Builds the top bar: "Estudios" toggle, then toolbar rows 1–26. */
export function install(shell: ViewerShell): void {
  installStudiesToggle(shell);
  const flags = viewerFlags(shell);
  const host = shell.regions.toolbar;
  const menuHost = shell.regions.overlayLayer;
  host.replaceChildren();
  host.classList.add('tb-root');
  const bar = document.createElement('div');
  bar.className = 'tb-items';
  host.appendChild(bar);

  dropdown(menuHost, bar, 'layout', 'Esquema', 'Cambiar distribución', layoutItems); // 1
  // The multi-cell features leave the compact strip for "Más" (compactItems); Mover and Magnificar join it.
  toggleButton(bar, 'link', 'Sincronizar', 'Sincronizar series', 'toggle-link', 'linked').classList.add('tb-compact-hide'); // 2
  toggleButton(bar, 'manual-link', 'Manual', 'Sincronizar series de diferentes estudios a partir de posición inicial', 'toggle-manual-link', 'manualLinked').classList.add('tb-compact-hide'); // 3

  const windowSplit = document.createElement('div'); // 4
  windowSplit.className = 'tb-split';
  bar.appendChild(windowSplit);
  const windowMain = toolButton(windowSplit, { tool: 'window-level', label: 'Ventana', title: '(w) Cambiar ventana y nivel de ventana, utilice los números para ventanas predeterminadas', id: 'window' });
  windowMain.classList.add('tb-split-main');
  const windowCaret = dropdown(menuHost, windowSplit, 'window-presets', '', 'Ventanas predeterminadas', () => windowItems(shell));
  windowCaret.classList.add('tb-split-caret');
  windowCaret.setAttribute('aria-label', 'Ventanas predeterminadas');

  dropdown(menuHost, bar, 'align', 'Alinear', 'Alinear la imagen', () => ALIGN_ITEMS.map(([label, command]) => ({ id: `${command.replace(':', '-').toLowerCase()}-link`, label, command }))).classList.add('tb-compact-hide'); // 5
  commandButton(bar, 'invert', 'Invertir', '(i) Invertir polaridad de la imagen', 'invert'); // 6
  toolButton(bar, { tool: 'stack', label: 'Avanzar', title: '(s) Avanzar en la serie', id: 'stack' }); // 7
  toolButton(bar, PAN_TOOL).classList.add('tb-wide-only', 'tb-touch-show'); // 8: no middle button on a touch screen
  toolButton(bar, ZOOM_TOOL).classList.add('tb-wide-only', 'tb-touch-show'); // 9: no right button on a touch screen
  const rotateSplit = document.createElement('div'); // 10
  rotateSplit.className = 'tb-split';
  bar.appendChild(rotateSplit);
  commandButton(rotateSplit, 'rotate', 'Rotar', 'rotar la imagen contra las manecillas del reloj', 'rotate').classList.add('tb-split-main');
  const rotateCaret = dropdown(menuHost, rotateSplit, 'rotate-options', '', 'Opciones de rotación', rotateItems, ['rotate-free']);
  rotateCaret.classList.add('tb-split-caret');
  rotateCaret.setAttribute('aria-label', 'Opciones de rotación');
  if (flags.flipToolsInTopMenu) {
    commandButton(bar, FLIP_H.id, FLIP_H.label, FLIP_H.title, FLIP_H.command); // 11
    commandButton(bar, FLIP_V.id, FLIP_V.label, FLIP_V.title, FLIP_V.command); // 12
  }
  commandButton(bar, MAXIMIZE.id, MAXIMIZE.label, MAXIMIZE.title, MAXIMIZE.command).classList.add('tb-wide-only'); // 13
  toolButton(bar, MAGNIFY_TOOL).classList.add('tb-wide-only'); // 14
  if (!flags.flipToolsInTopMenu) {
    toolButton(bar, LINE_TOOL); // 15
    toolButton(bar, ANGLE_TOOL); // 16
  }
  dropdown(menuHost, bar, 'roi', 'ROI', '', () => ROI_TOOLS.map(toolItem), ROI_TOOLS.map((t) => t.tool)); // 17
  if (flags.advancedAnnotationsEnabled) {
    const measureTools = MEASURE_TOOLS.map((t) => t.tool);
    if (flags.flipToolsInTopMenu) measureTools.push('lines', 'angles');
    dropdown(menuHost, bar, 'measures', 'Medidas', '', () => measureItems(flags), measureTools); // 18
  }
  toolButton(bar, { tool: 'triangulation', label: 'Triangulación', title: '(t) Triangulación (Herramienta 3D)', id: 'triangulation' }).classList.add('tb-compact-hide'); // 19
  toolButton(bar, { tool: 'erase', label: 'Borrar', title: '(e) Borrar mediciones', id: 'erase' }); // 20
  toggleButton(bar, 'reference', 'Referencia', 'Líneas de referencia', 'toggle-reference-lines', 'referenceLines').classList.add('reference-button'); // 21
  toolButton(bar, { tool: 'probe', label: 'Densidad', title: '(d) Medir densidad de un punto', id: 'probe' }); // 22
  if (flags.keyImagesModule && isCommandAllowed('key-images')) {
    const star = commandButton(bar, 'key-images', '', 'Seleccionar imagen de las imágenes clave', 'key-images'); // 23
    star.classList.add('tb-icon', 'tb-star');
    star.setAttribute('aria-label', 'Imágenes clave');
  }
  if (flags.segmentationModule && isCommandAllowed('ai-module')) commandButton(bar, 'ai-module', 'Módulo IA', 'Desplegar módulos IA disponibles', 'ai-module'); // 24
  const more = dropdown(menuHost, bar, 'more', 'Más', '', () => moreItems(shell, flags)); // 25
  more.classList.add('tb-more');
  mountCineBar(bar); // 26

  refresh(host);
  onLeftToolChange(() => refresh(host));
  onCommandStateChange(() => refresh(host));
}
